import { useSyncExternalStore } from 'react';
import { en } from './en';
import { zh } from './zh';

/**
 * Язык интерфейса. Каждый выбирает свой: выбор хранится в браузере этого пользователя
 * и не влияет на других.
 *
 * Ключ перевода — сама русская фраза. {n}, {no} и т.п. — подстановки.
 * t() — для текстов интерфейса, tx() — для готовых текстов с сервера (статусы, ошибки,
 * предпросмотр, журнал): ищет точное совпадение, затем шаблон с подстановками.
 * Если перевода нет, остаётся русский текст — ничего не ломается.
 */
export type Lang = 'ru' | 'en' | 'zh';

export const LANGS: { id: Lang; short: string; title: string }[] = [
  { id: 'ru', short: 'RU', title: 'Русский' },
  { id: 'en', short: 'EN', title: 'English' },
  { id: 'zh', short: '中文', title: '中文' }
];

const KEY = 'parts.lang';
const DICTS: Record<Exclude<Lang, 'ru'>, Record<string, string>> = { en, zh };
const HTML_LANG: Record<Lang, string> = { ru: 'ru', en: 'en', zh: 'zh-CN' };

function readStored(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'ru' || v === 'en' || v === 'zh') return v;
  } catch {
    /* нет доступа к хранилищу браузера */
  }
  return 'ru';
}

let current: Lang = readStored();
const listeners = new Set<() => void>();
document.documentElement.lang = HTML_LANG[current];

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    /* не сохранится до перезагрузки — не страшно */
  }
  document.documentElement.lang = HTML_LANG[lang];
  listeners.forEach((l) => l());
}

export function useLang(): Lang {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getLang
  );
}

type Vars = Record<string, string | number>;

function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Перевод фразы интерфейса. */
export function t(key: string, vars?: Vars): string {
  const dict = current === 'ru' ? null : DICTS[current];
  return fill(dict?.[key] ?? key, vars);
}

/** Число и слово в нужной форме: tn(5, ['позиция', 'позиции', 'позиций']) → «5 позиций» / «5 positions» / «5 项». */
export function tn(n: number, forms: [string, string, string]): string {
  if (current === 'ru') {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    const word = a > 10 && a < 20 ? forms[2] : b > 1 && b < 5 ? forms[1] : b === 1 ? forms[0] : forms[2];
    return `${n} ${word}`;
  }
  return `${n} ${t(n === 1 ? forms[0] : forms[2])}`;
}

interface Pattern {
  re: RegExp;
  names: string[];
  out: string;
}

const patternCache: Partial<Record<Lang, Pattern[]>> = {};

function patterns(lang: Exclude<Lang, 'ru'>): Pattern[] {
  const cached = patternCache[lang];
  if (cached) return cached;
  const list: Pattern[] = [];
  for (const [key, out] of Object.entries(DICTS[lang])) {
    if (!/\{\w+\}/.test(key)) continue;
    const names: string[] = [];
    const source = key
      .split(/(\{\w+\})/)
      .map((part) => {
        const m = part.match(/^\{(\w+)\}$/);
        if (m) {
          names.push(m[1]!);
          return '([\\s\\S]+?)';
        }
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      })
      .join('');
    list.push({ re: new RegExp(`^${source}$`), names, out });
  }
  // Сначала самые длинные (конкретные) шаблоны
  list.sort((a, b) => b.re.source.length - a.re.source.length);
  patternCache[lang] = list;
  return list;
}

/** Перевод готового текста с сервера или из общих правил. Незнакомое остаётся как есть. */
export function tx(text: string | null | undefined, depth = 0): string {
  if (!text) return text ?? '';
  if (current === 'ru') return text;
  const dict = DICTS[current];
  const exact = dict[text];
  if (exact !== undefined) return exact;
  if (depth > 3) return text;
  for (const p of patterns(current)) {
    const m = text.match(p.re);
    if (!m) continue;
    const vars: Vars = {};
    p.names.forEach((name, i) => {
      vars[name] = tx(m[i + 1] ?? '', depth + 1);
    });
    return fill(p.out, vars);
  }
  // Составные строки «часть · часть» и «было → стало» переводим по частям
  if (text.includes(' · ')) return text.split(' · ').map((s) => tx(s, depth + 1)).join(' · ');
  if (text.includes(' → ')) return text.split(' → ').map((s) => tx(s, depth + 1)).join(' → ');
  return text;
}

/** Хук для компонентов: перерисовка при смене языка. */
export function useI18n() {
  const lang = useLang();
  return { lang, t, tx, tn };
}
